/**
 * utils/remoteSync.js
 * The sync engine for the shared online books (multi-device sync, steps 4 + 5a). No React.
 *
 * App.js hands every change to `apply(fn, meta)` instead of running it itself. The engine runs
 * the change on its current data, shows the result (onData), remembers the change, and saves
 * the whole data object with the version check (utils/remoteStorage.js).
 *
 * SAVING (unchanged from step 4)
 *   - waits `debounceMs` after the last change; never two saves in flight;
 *   - no answer (offline / timeout / other errors) → keeps everything and retries with growing
 *     delays until it gets through;
 *   - stops — keeping the unsaved data and reporting it — when the stored data is in a NEWER
 *     format ("outdated"), when the save is refused as too large / invalid ("rejected"), or
 *     when merging keeps failing ("conflict").
 *
 * MERGING (step 5a — what replaced step 4's "stop on conflict")
 * When the save is refused because someone else saved first, the engine downloads their
 * newer books and RE-APPLIES every change of ours that the server hasn't confirmed yet, in
 * order, on top of them — exactly as if the user had made those changes just now — then saves
 * again. Each re-applied change:
 *   - gets the SAME ids it got the first time (idGenerators.withIdReplay), so a record the
 *     screen already holds by id still exists under that id;
 *   - re-derives everything else from the newer books — so stock deltas land on the newest
 *     stock, and invoice numbers / item codes are minted from the newest counters (two sales
 *     made at the same moment can never share a number);
 *   - is NOT re-applied, and is reported instead, when:
 *       · meta.touches says it was editing a record that someone else changed meanwhile
 *         (decision 2026-10-08: "stop and tell" — never silently overwrite their edit);
 *       · meta.replay === false (built from a snapshot of the whole books — the Excel imports);
 *       · the change now gets refused (it returns the data unchanged — e.g. the contact it
 *         used was deleted meanwhile);
 *       · meta.newItemCodes === "same" and it would now create items under different codes
 *         than the screen used (Transformasi's inline-created items).
 * Changes that fail are dropped (the screen switches to the merged data without them) and
 * listed in a notice; every other change is kept and saved. Nothing is ever dropped silently.
 *
 * meta (all optional):
 *   label        string | (data) => string — names the change in messages
 *   touches      [{ get: (data) => value, what: string, describe?: (before, now) => string }]
 *                — the record(s) this change edits; compared before re-applying
 *   replay       false → never re-applied automatically
 *   newItemCodes "same" | "notify" — see above / report a changed new item code
 *   localOnly    true → shown and kept, but doesn't trigger a save by itself (rides along with
 *                the next real save) — for UI-only writes like category visit counts
 *
 * SEEING OTHER PEOPLE'S CHANGES (step 5b): checkForUpdates(canApply) — see that function.
 *
 * STATUS via onStatus(status), and NOTICES via onNotice(notice) — see the end of this file.
 */
import { withIdRecording, withIdReplay } from "./idGenerators";

const RETRY_DELAYS = [2000, 5000, 10000, 30000]; // then every 30 s
const MAX_MERGES_IN_A_ROW = 5;
const STOPPED = new Set(["conflict", "outdated", "rejected"]);

/** Deep equality that ignores key order (Postgres jsonb reorders keys) and treats a missing key
 *  the same as an `undefined` one (JSON drops undefined values). */
export function sameValue(a, b) {
  if (a === b) return true;
  if (a === null || b === null || typeof a !== "object" || typeof b !== "object") {
    return (a === undefined && b === undefined) || (a === null && b === null) || a === b;
  }
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  if (Array.isArray(a)) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (!sameValue(a[i], b[i])) return false;
    return true;
  }
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const k of keys) {
    if (a[k] === undefined && b[k] === undefined) continue;
    if (!sameValue(a[k], b[k])) return false;
  }
  return true;
}

const itemCodes = (d) => new Set(((d && d.items) || []).map((it) => it.internalCode));
const newCodes = (before, after) => {
  const had = itemCodes(before);
  return [...itemCodes(after)].filter((c) => !had.has(c)).sort();
};

export function createSyncEngine({
  store,                       // createRemoteStore(...) — needs saveState + getState
  data,                        // the books as loaded
  version,                     // the version `data` is
  normVersion,                 // this app's NORM_VERSION
  prepare = (d) => d,          // upgrade downloaded data (storage.js's prepareLoadedData)
  getSavedBy = () => null,
  onData = () => {},           // the books changed (own change, or a merge)
  onStatus = () => {},
  onNotice = () => {},
  debounceMs = 500,
  retryDelays = RETRY_DELAYS,
}) {
  let current = data;
  let baseVersion = version;
  let changes = [];            // not yet confirmed by the server: { seq, fn, meta, ids, label, before, after, needsSave }
  let seq = 0;
  let busy = false;            // a save or a merge is in progress (never both, never two)
  let timer = null;
  let retryCount = 0;
  let mergesInARow = 0;
  let stopped = null;
  let lastStatus = { state: "ok" };
  let nextAction = "save";     // what the timer runs: "save" | "merge"
  let pendingConflictBy = null;
  let waitingVersion = null;   // 5b: a newer version seen while a form was open

  const report = (s) => { lastStatus = s; onStatus(s); };
  const needsSaving = () => changes.some((c) => c.needsSave);
  const schedule = (ms, action = "save") => {
    clearTimeout(timer);
    nextAction = action;
    timer = setTimeout(run, ms);
  };
  const stop = (s) => { stopped = s; clearTimeout(timer); timer = null; report(s); };

  function run() {
    timer = null;
    if (nextAction === "merge") merge(); else flush();
  }

  function retryLater(r) {
    const kind = r && r.error ? r.error.kind : "http";
    const delay = retryDelays[Math.min(retryCount, retryDelays.length - 1)];
    retryCount += 1;
    report(kind === "offline" || kind === "timeout"
      ? { state: "offline", message: r && r.error ? r.error.message : "" }
      : { state: "error", kind, message: r && r.error ? r.error.message : "" });
    return delay;
  }

  async function flush() {
    if (stopped || busy || !needsSaving()) return;
    const dataToSave = current;
    const upToSeq = seq;
    busy = true;
    const r = await store.saveState({ expectedVersion: baseVersion, data: dataToSave, normVersion, savedBy: getSavedBy() });
    busy = false;
    if (stopped) return;

    if (r.ok) {
      baseVersion = r.version;
      retryCount = 0;
      mergesInARow = 0;
      changes = changes.filter((c) => c.seq > upToSeq);
      if (needsSaving()) { report({ state: "pending" }); schedule(0); } else report({ state: "ok" });
      return;
    }
    if (r.reason === "conflict") {
      pendingConflictBy = r.updatedBy || null;
      merge();
      return;
    }
    if (r.reason === "outdated_app") { stop({ state: "outdated" }); return; }
    if (r.reason === "too_large" || r.reason === "invalid") {
      stop({
        state: "rejected",
        reason: r.reason,
        message: r.reason === "too_large"
          ? `Data terlalu besar untuk disimpan (${Math.round((r.bytes || 0) / 1048576)} MB, batas ${Math.round((r.limit || 0) / 1048576)} MB).`
          : "Server menolak data ini.",
      });
      return;
    }
    schedule(retryLater(r), "save");
  }

  /** Re-apply one change on `d`. Returns { result } or { failure }. */
  function reapply(change, d) {
    const meta = change.meta || {};
    for (const t of meta.touches || []) {
      const was = t.get(change.before);
      const now = t.get(d);
      if (!sameValue(was, now)) {
        return { failure: { label: change.label, reason: "changed", what: t.what, detail: t.describe ? t.describe(was, now) : null } };
      }
    }
    if (meta.replay === false) return { failure: { label: change.label, reason: "cannot_merge" } };
    let result;
    try {
      result = withIdReplay(change.ids, () => change.fn(d));
    } catch (e) {
      return { failure: { label: change.label, reason: "refused" } };
    }
    if (result === d) return { failure: { label: change.label, reason: "refused" } };
    const wanted = newCodes(change.before, change.after);
    const got = newCodes(d, result);
    if (meta.newItemCodes === "same" && !sameValue(wanted, got)) {
      return { failure: { label: change.label, reason: "code_taken", codes: wanted } };
    }
    const codeChange = meta.newItemCodes === "notify" && wanted.length === 1 && got.length === 1 && wanted[0] !== got[0]
      ? { label: change.label, from: wanted[0], to: got[0] }
      : null;
    return { result, codeChange };
  }

  async function merge() {
    if (stopped || busy) return;
    mergesInARow += 1;
    if (mergesInARow > MAX_MERGES_IN_A_ROW) {
      stop({ state: "conflict", updatedBy: pendingConflictBy });
      return;
    }
    report({ state: "pending", merging: true });
    busy = true;
    const g = await store.getState();
    busy = false;
    if (stopped) return;
    if (!g.ok) { schedule(retryLater(g), "merge"); return; }
    retryCount = 0;
    if (g.normVersion !== null && g.normVersion > normVersion) { stop({ state: "outdated" }); return; }
    if (g.data === null) {
      stop({ state: "rejected", reason: "invalid", message: "Data bersama di server kosong secara tak terduga." });
      return;
    }

    adoptBooks(g, "merged");
  }

  /** Take on the downloaded books `g` and re-apply every unconfirmed change of ours on top
   *  (5a merge, and 5b refresh). Synchronous, so no change of ours can slip in between. */
  function adoptBooks(g, kind) {
    let d = prepare(g.data);
    const kept = [];
    const failures = [];
    const codeChanges = [];
    for (const change of changes) {
      const out = reapply(change, d);
      if (out.failure) { failures.push(out.failure); continue; }
      change.before = d;
      change.after = out.result;
      d = out.result;
      kept.push(change);
      if (out.codeChange) codeChanges.push(out.codeChange);
    }
    changes = kept;
    current = d;
    baseVersion = g.version;
    onData(d);
    const keptCount = kept.filter((c) => c.needsSave).length;
    const by = g.updatedBy || (kind === "merged" ? pendingConflictBy : null) || null;
    onNotice({ kind, updatedBy: by, failures, codeChanges, keptCount });
    if (needsSaving()) { report({ state: "pending" }); schedule(0); } else report({ state: "ok" });
  }

  /**
   * 5b — pick up other people's changes without saving or reloading. Asks the server only for
   * the version number (tiny); downloads the books ONLY when it changed. Skipped while anything
   * of ours is still being saved (that save merges anyway), and held back while `canApply()`
   * says the screen must not change under the user (a form or window is open — decision
   * 2026-10-08): the newer version is then remembered (`hasWaitingUpdate`) and taken on by the
   * next check once the form closes. A failed check reports nothing — the next one simply tries
   * again; a real connection problem surfaces as soon as something is saved.
   * Resolves to "skipped" | "current" | "waiting" | "refreshed" | "outdated".
   */
  async function checkForUpdates(canApply = () => true) {
    if (stopped || busy || needsSaving()) return "skipped";
    const result = await check(canApply);
    // A change made while the check was running was told "the busy job will pick this up" —
    // so if the check didn't (it only does when it takes on newer books), start its save now.
    if (!stopped && needsSaving() && timer === null && !busy) schedule(debounceMs);
    return result;
  }

  async function check(canApply) {
    busy = true;
    const v = await store.getVersion();
    busy = false;
    if (stopped || !v || !v.ok) return "skipped";
    if (v.normVersion !== null && v.normVersion > normVersion) { stop({ state: "outdated" }); return "outdated"; }
    if (v.version === baseVersion) { waitingVersion = null; return "current"; }
    if (!canApply()) { waitingVersion = v.version; return "waiting"; }
    if (needsSaving()) return "skipped"; // our own save will merge their changes anyway
    busy = true;
    const g = await store.getState();
    busy = false;
    if (stopped || !g || !g.ok || g.data === null) return "skipped";
    if (g.normVersion !== null && g.normVersion > normVersion) { stop({ state: "outdated" }); return "outdated"; }
    if (g.version === baseVersion) { waitingVersion = null; return "current"; }
    if (!canApply()) { waitingVersion = g.version; return "waiting"; } // a form opened meanwhile
    waitingVersion = null;
    mergesInARow = 0;
    adoptBooks(g, "refreshed"); // re-applies any change made during the download, and saves it
    return "refreshed";
  }

  return {
    /** Make a change: run `fn` on the current books, show the result, and save it. */
    apply(fn, meta = {}) {
      const before = current;
      const { result, ids } = withIdRecording(() => fn(before));
      if (result === before) return; // refused / nothing changed — nothing to show or save
      const label = typeof meta.label === "function" ? meta.label(before) : (meta.label || "Perubahan");
      changes.push({ seq: ++seq, fn, meta, ids, label, before, after: result, needsSave: !meta.localOnly });
      current = result;
      onData(result);
      if (meta.localOnly) return;
      if (stopped) { report(stopped); return; }
      if (lastStatus.state !== "offline" && lastStatus.state !== "error") report({ state: "pending" });
      // A save or merge in progress picks this change up when it finishes; a retry timer
      // already waiting will carry it. Otherwise start the usual short wait.
      if (busy || (timer !== null && (retryCount > 0 || nextAction === "merge"))) return;
      schedule(debounceMs);
    },
    /** The books as currently shown. */
    getData() { return current; },
    /** 5b: check for other people's changes and show them (see checkForUpdates above). */
    checkForUpdates,
    /** 5b: a newer version is known, held back because a form was open. */
    hasWaitingUpdate() { return waitingVersion !== null; },
    /** True while any change isn't confirmed by the server yet (queued, saving, retrying, stopped). */
    hasUnsaved() { return needsSaving() || busy; },
    isStopped() { return stopped !== null; },
    getVersion() { return baseVersion; },
    getStatus() { return lastStatus; },
    dispose() { clearTimeout(timer); timer = null; },
  };
}

export const isStoppedState = (state) => STOPPED.has(state);

/*
 * STATUS objects:
 *   { state: "ok" }                          everything saved
 *   { state: "pending", merging? }           saving, or merging with someone else's changes
 *   { state: "offline", message }            no connection — retrying
 *   { state: "error", kind, message }        another no-answer error — retrying
 *   { state: "conflict", updatedBy }         stopped — merging kept failing
 *   { state: "outdated" }                    stopped — stored data is in a newer format
 *   { state: "rejected", reason, message }   stopped — the save was refused
 *
 * NOTICE objects (after a merge, or after picking up someone else's changes):
 *   { kind: "merged" | "refreshed", updatedBy, keptCount,
 *     failures: [{ label, reason: "changed"|"cannot_merge"|"refused"|"code_taken", what?, detail?, codes? }],
 *     codeChanges: [{ label, from, to }] }
 */
